// components/auth/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetPassword, clearError, clearPasswordResetState } from '../../slices/authSlice.mjs';
import { setPageTitle, setPageDescription } from '../../slices/pageMetadataSlice.mjs';
import { setHeaderData } from '../../slices/headerSlice.mjs';

import useIsMounted from '../../hooks/useIsMounted.mjs';

const ClientSideResetHandler = ({ resetSuccess }) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (resetSuccess) {
            const timer = setTimeout(() => {
                navigate('/login');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [resetSuccess, navigate]);

    return null;
};

const ResetPasswordContent = () => {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [validationErrors, setValidationErrors] = useState({});

    const { loading, error, fieldErrors, passwordResetSuccess } = useSelector(
        (state) => state.auth || {}
    );
    const { token } = useParams();
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    useEffect(() => {
        dispatch(setPageTitle('Reset Password - Nexus of Truth'));
        dispatch(setPageDescription('Create a new password for your account.'));
        dispatch(
            setHeaderData({
                title: 'Reset Password',
                description: 'Create a new password.',
            })
        );

        // Clear password reset state when component mounts
        dispatch(clearPasswordResetState());

        // Clear errors when component unmounts
        return () => {
            if (error) {
                dispatch(clearError());
            }
        };
    }, [dispatch, error]);

    // Handle API errors
    useEffect(() => {
        if (fieldErrors) {
            setValidationErrors((prev) => ({
                ...prev,
                ...fieldErrors,
            }));
        }
    }, [fieldErrors]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: '',
            });
        }
        if (error) {
            dispatch(clearError());
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        dispatch(
            resetPassword({
                token,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
            })
        );
    };

    if (passwordResetSuccess) {
        return (
            <>
                {isMounted && <ClientSideResetHandler resetSuccess={passwordResetSuccess} />}
                <div className="auth-container">
                    <div className="auth-form-wrapper">
                        <div className="success-message">
                            <h1>Password Reset Successful</h1>
                            <p>Your password has been successfully reset.</p>
                            <p>You will be redirected to the login page in a few seconds...</p>
                            <div className="auth-links">
                                {isMounted ? (
                                    <Link to="/login">Go to Login Now</Link>
                                ) : (
                                    <a href="/login">Go to Login Now</a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="auth-container">
                <div className="auth-form-wrapper">
                    <h1>Reset Password</h1>

                    {error && <div className="error-message">{error}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="password">New Password</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={validationErrors.password ? 'error' : ''}
                            />
                            {validationErrors.password && (
                                <div className="error-text">{validationErrors.password}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={validationErrors.confirmPassword ? 'error' : ''}
                            />
                            {validationErrors.confirmPassword && (
                                <div className="error-text">{validationErrors.confirmPassword}</div>
                            )}
                        </div>

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </form>

                    <div className="auth-links">
                        {isMounted ? (
                            <Link to="/login">Back to Login</Link>
                        ) : (
                            <a href="/login">Back to Login</a>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPasswordContent;
